import React from 'react';
import Plot from 'react-plotly.js';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { COLOR_PALETTE_ROADS } from 'diagrammColors';
import { DIAGRAM_COLORS } from 'diagrammColors';

const plotLayout = (title, yMax = null) => ({
  title: {
    text: title,
    font: { color: 'white', size: 16 },
    x: 0.5,
    xanchor: 'center'
  },
  margin: { l: 40, r: 20, t: 30, b: 50 },
  paper_bgcolor: 'rgba(0,0,0,0)',
  plot_bgcolor: 'rgba(0,0,0,0)',
  font: { color: 'white' },
  xaxis: {
    color: 'white',
    tickfont: { color: 'white' },
    tickangle: -45,
    automargin: true
  },
  yaxis: {
    color: 'white',
    tickfont: { color: 'white' },
    automargin: true,
    ...(yMax !== null ? { range: [0, yMax * 1.2] } : {})
  },
  legend: {
    orientation: 'h',
    y: -0.2,
    x: 0.5,
    xanchor: 'center'
  }
});

const simulateBoxData = (stat) => {
  const data = [];
  const safePush = (val, times = 3) => {
    if (val != null) for (let i = 0; i < times; i++) data.push(val);
  };
  safePush(stat.min, 1);
  safePush(stat.q1, 3);
  safePush(stat.median, 3);
  safePush(stat.q3, 3);
  safePush(stat.max, 1);
  return data;
};

const extractBoxComparisonPlot = (stats, titlePrefix, t) => {
  const traces = [];
  let yMax = 0;

  const scenarios = ['baseline', 'acyclic', 'nema'];
  const scenarioLabel = (scenario) => t(`sumo.scenarios.${scenario}`);
  const isNested = typeof stats['baseline'] === 'object' &&
    Object.values(stats['baseline']).some(v => v && typeof v === 'object' && 'q1' in v);

  if (isNested) {
    const vehicleTypes = Object.keys(stats['baseline']);
    vehicleTypes.forEach((vehicleType) => {
      scenarios.forEach((scenario) => {
        const stat = stats?.[scenario]?.[vehicleType];
        if (!stat || stat.q1 == null || stat.median == null || stat.q3 == null) return;

        const label = `${vehicleType.toUpperCase()} - ${scenarioLabel(scenario)}`;
        const boxY = simulateBoxData(stat);
        yMax = Math.max(yMax, stat.max || 0, ...(stat.outliers || []));

        traces.push({
          x: Array(boxY.length).fill(label),
          y: boxY,
          type: 'box',
          name: label,
          boxpoints: false,
          marker: { color: DIAGRAM_COLORS[scenarios.indexOf(scenario)] || 'gray' },
          line: { color: DIAGRAM_COLORS[scenarios.indexOf(scenario)] || 'gray' }

        });

        if (stat.outliers?.length > 0) {
          traces.push({
            x: Array(stat.outliers.length).fill(label),
            y: stat.outliers,
            type: 'scatter',
            mode: 'markers',
            name: `${label} ${t("sumo.charts.outliersSuffix")}`,
            marker: { symbol: 'circle', size: 5, color: 'orange' },
            hoverinfo: 'y'
          });
        }
      });
    });
  } else {
    scenarios.forEach((scenario) => {
      const stat = stats?.[scenario];
      if (!stat || stat.q1 == null || stat.median == null || stat.q3 == null) return;

      const label = scenarioLabel(scenario);
      const boxY = simulateBoxData(stat);
      yMax = Math.max(yMax, stat.max || 0, ...(stat.outliers || []));
      //yMin = Math.min(yMin, stat.min ?? yMin);
      traces.push({
        x: Array(boxY.length).fill(label),
        y: boxY,
        type: 'box',
        name: label,
        boxpoints: false,
        marker: { color: DIAGRAM_COLORS[scenarios.indexOf(scenario)] || 'gray' },
        line: { color: DIAGRAM_COLORS[scenarios.indexOf(scenario)] || 'gray' }
      });

      if (stat.outliers?.length > 0) {
        traces.push({
          x: Array(stat.outliers.length).fill(label),
          y: stat.outliers,
          type: 'scatter',
          mode: 'markers',
          name: `${label} ${t("sumo.charts.outliersSuffix")}`,
          marker: { symbol: 'circle', size: 5, color: 'orange' },
          hoverinfo: 'y'
        });
      }
    });
  }
  //const yMinFinal = yMin < 2 ? yMin * 0.95 : null;

  return (
    <Grid item lg={12} md={12} xs={12} key={titlePrefix}>
      <Plot
        data={traces}
        layout={plotLayout(titlePrefix, yMax)}
        useResizeHandler
        style={{ width: '100%', height: '450px' }}
        config={{ responsive: true }}
      />
    </Grid>
  );
};

const extractThroughputBarChart = (throughputData, t) => {
  const baseline = throughputData?.baseline;
  const acyclic = throughputData?.acyclic;
  const nema = throughputData?.nema;
  let yMax = Math.max(baseline * 1.3, acyclic * 1.3, nema * 1.3);

  let traces = [];
  if (baseline){
    traces.push({
      x: ['baseline'],
      y: [baseline],
      type: 'bar',
      name: t("sumo.scenarios.baseline"),
      marker: { color: DIAGRAM_COLORS[0] }
    })
  }
  if(acyclic){
    traces.push({
      x: ['acyclic'],
      y: [acyclic],
      type: 'bar',
      name: t("sumo.scenarios.acyclic"),
      marker: { color: DIAGRAM_COLORS[1] }
    })
  }
  if(nema){
    traces.push({
      x: ['nema'],
      y: [nema],
      type: 'bar',
      name: t("sumo.scenarios.nema"),
      marker: { color: DIAGRAM_COLORS[2] }
    })
  }


  return (
    <Grid item lg={12} md={12} xs={12} key="Throughput">
      <Plot
        data={traces}
        layout={plotLayout(t("sumo.charts.throughput"), yMax)}
        useResizeHandler
        style={{ width: '100%', height: '450px' }}
        config={{ responsive: true }}
      />
    </Grid>
  );
};

const ChildChart = ({ data }) => {
  const { t } = useTranslation();
  const charts = [];

  const mapBlock = [
    ['meanTravelTimePerVehicle', 'meanTravelTime', t("sumo.charts.meanTravelTimePerVehicle")],
    ['meanTravelTimePerVehicleType', 'meanTravelTimePerType', t("sumo.charts.meanTravelTimePerVehicleType")],
    ['waitingTimePerVehicle', 'waitingTime', t("sumo.charts.waitingTimePerVehicle")],
    ['waitingTimePerVehicleType', 'waitingTime', t("sumo.charts.waitingTimePerVehicleType")],
    ['waitingCountPerVehicle', 'waitingCount', t("sumo.charts.waitingCountPerVehicle")],
    ['waitingCountPerVehicleType', 'waitingCount', t("sumo.charts.waitingCountPerVehicleType")],
    ['speedAvgPerVehicle', 'speedAvg', t("sumo.charts.speedAvgPerVehicle")],
    ['speedAvgPerVehicleType', 'speedAvg', t("sumo.charts.speedAvgPerVehicleType")]
  ];

  mapBlock.forEach(([key1, key2, title]) => {
    if (data[key1]?.[key2]) {
      charts.push(extractBoxComparisonPlot(data[key1][key2], title, t));
    }
  });

  if (data.throughput?.throughput) {
    charts.push(extractThroughputBarChart(data.throughput.throughput, t));
  }

  return charts;
};

export default ChildChart;
